// @mui imports
import {
   Box,
   Button,
   Card,
   CardActions,
   CardContent,
   Collapse,
   Container,
   Divider,
   FormLabel,
   ListItemText,
   MenuItem,
   Radio,
   RadioGroup,
   Typography,
   IconButton,
   FormControl,
   CssBaseline,
   FormControlLabel,
   Tooltip,
   Paper,
   Stack,
} from "@mui/material";
// @mui icons imports
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import HelpIcon from "@mui/icons-material/Help";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
// imports
import { useContext, useEffect, useState, useRef, useMemo } from "react";
import { categoryOptions, complainerTypeOptions } from "../const/complaintsConst";
import { CompanyContext } from "../context/companyContext";
import { NavBar } from "../components/NavBar";
import { ThankYouModal } from "../components/ThankYouModal";
import { ComplainerDetailsModal } from "../components/ComplainerDetailsModal";
import { NewComplaintBar } from "../components/NewComplaintBar";
import { createComplaint } from "../lib/lecosyBackendClient";
import { useParams } from "react-router-dom";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { WitnessInfractorModal } from "../components/WitnessInfractorModal";
import { Formik, Form, FormikProps } from "formik";
import { InputTextField } from "../components/Formik Components/InputTextField";
import { InputSelectField } from "../components/Formik Components/InputSelectField";
import { complaintFormSchema, niggleFormSchema, sugestionFormSchema } from "../const/SchemaValidations";
import { SnackBarContext } from "../context/snackBarContext";
import { SnackBarComponent } from "../components/snackBarComponent";
import Compressor from "compressorjs";
import { InputCheckBox } from "../components/Formik Components/InputCheckBox";
import { uploads3File } from "../lib/s3BackendClient";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

export const NewComplaintScreen = () => {
   const { getInfo, company, userClient } = useContext(CompanyContext);
   const { showSnackBar } = useContext(SnackBarContext);
   const { subName, type } = useParams();
   const evidenceRef: any = useRef(null);
   const [nonAnonymousFormVisible, setNonAnonymousFormVisible] = useState(false);
   const [offenderFormOpen, setOffenderFormOpen] = useState(false);
   const [witnessFormOpen, setWitnessFormOpen] = useState(false);
   const [editWitnessIndex, setEditWitnessIndex] = useState<number | null>(null);
   const [thankyouModalVisible, setThankyouModalVisible] = useState(false);
   const [complaintResultInfo, setComplaintResultInfo] = useState({
      rn: "",
      vc: "",
   });
   const [evidenceBlob, setEvidenceBlob] = useState<File[]>([]);
   const [evidences, setEvidences] = useState<string[]>([]);
   const [hasEvidence, setHasEvidence] = useState(false);
   const [isSendingComplaint, setIsSendingComplaint] = useState(false);

   const validationSchema = useMemo(() => {
      if (type === "denuncia") return complaintFormSchema;
      if (type === "queja") return niggleFormSchema;
      return sugestionFormSchema;
   }, [type]);

   const descriptionHolder = useMemo(() => {
      if (type === "denuncia")
         return "Indicar al mayor detalle posible qué, cómo, cuándo y dónde sucedió el incidente, quiénes están dos (nombre y puesto), así como lo que obtienen. Se debe incluir cualquier información que pueda ser valiosa en la evaluación y resolución de esta situación.";
      if (type === "sugerencia")
         return "Tu opinión es importante para nosotros. Describa su sugerencia con claridad y precisión, incluyendo cualquier detalle que considere relevante.";
      return "Proporcione una descripción detallada de su queja, incluyendo todos los eventos relevantes, fechas y cualquier otra información pertinente que nos ayude a entender completamente su situación.";
   }, [type]);
   useEffect(() => {
      getInfo(subName);
   }, []);

   const onEvidenceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      try {
         const files = Array.from(e.target.files || []) as File[];
         if (files.length === 0) return;

         const MAX_FILES = 10;
         if (evidenceBlob.length + files.length > MAX_FILES) {
            showSnackBar(`Límite de ${MAX_FILES} archivos alcanzado.`, true);
            return;
         }

         for (const file of files) {
            if (file.type.startsWith("image")) {
               new Compressor(file, {
                  quality: 0.75,
                  success(result: File) {
                     if (result.size > 10000000) {
                        showSnackBar(`${result.name}: Archivo mayor a 10 MB`, true);
                        return;
                     }
                     setEvidenceBlob((prev: File[]) => [...prev, result]);
                     setEvidences((prev: string[]) => [...prev, result.name]);
                     setHasEvidence(true);
                  },
                  error(err) {
                     console.error({ err });
                     showSnackBar(`Error al procesar ${file.name}`, true);
                  },
               });
            } else {
               if (file.type.startsWith("video") && file.size > 100000000) {
                  showSnackBar(`${file.name}: Video mayor a 100 MB`, true);
                  continue;
               }
               if (file.type.includes("pdf") && file.size > 100000000) {
                  showSnackBar(`${file.name}: PDF mayor a 100 MB`, true);
                  continue;
               }
               setEvidenceBlob((prev: File[]) => [...prev, file]);
               setEvidences((prev: string[]) => [...prev, file.name]);
               setHasEvidence(true);
            }
         }

         if (evidenceRef.current) {
            evidenceRef.current.value = "";
         }
      } catch (error) {
         showSnackBar("Error, intente de nuevo.", true);
      }
   };

   async function handleNewComplaintFormik(values, actions) {
      try {
         setIsSendingComplaint(true);
         const complaintRepsonse = await createComplaint(
            type,
            company.subName,
            company.intern?.includes(type as "denuncia" | "queja" | "sugerencia"),
            values.followUpEmail,
            values.complaintType,
            values.complainerType,
            values.body,
            values.category,
            values.date,
            values.specificPlace,
            values.present,
            values.directlyAffected,
            company.group,
            company.id,
            values.branch,
            values.pseudonym,
            values.complainerDetails,
            values.witnesses.length > 0 ? values.witnesses : undefined,
            evidences.length > 0 ? evidences : undefined,
            values.offenderDetails.length > 0 ? values.offenderDetails : undefined,
         );
         if (complaintRepsonse.data.message === "Branch does not exists") {
            showSnackBar("Esta empresa no cuenta con sucursales aún.", true);
            return;
         }
         if (complaintRepsonse.status !== 201) {
            showSnackBar("Error, intente de nuevo", true);
            return;
         }
         const complaitResponseObtained = complaintRepsonse.data;
         if (evidenceBlob.length > 0) {
            await Promise.all(
               evidenceBlob.map((blob) =>
                  uploads3File(
                     "files-lecosy",
                     `evidences/${complaitResponseObtained.reportNumber}/Denunciado/${
                        complaitResponseObtained?.complainerDetails?._id ?? "anonimo"
                     }/${blob.name}`,
                     blob,
                     blob.type,
                  ),
               ),
            );
         }
         showSnackBar("Denuncia enviada con éxito", false);
         setComplaintResultInfo({
            ...complaintResultInfo,
            rn: complaitResponseObtained.reportNumber,
            vc: complaitResponseObtained.verificationCode,
         });
         setThankyouModalVisible(true);
         setIsSendingComplaint(false);
         actions.resetForm();
      } catch (error) {
         showSnackBar("Ocurrió un error al procesar la información, favor de reintentar.", true);
         setIsSendingComplaint(false);
      }
   }

   const openManual = () => {
      window.open("https://images-lecosy.s3.us-west-2.amazonaws.com/manuals/manual-Interposicion.pdf", "_blank");
   };

   const [editOffenderIndex, setEditOffenderIndex] = useState<number | null>(null);

   const handleEditWitness = (index: number) => {
      setEditWitnessIndex(index);
      setWitnessFormOpen(true);
   };

   const handleEditOffender = (index: number) => {
      setEditOffenderIndex(index);
      setOffenderFormOpen(true);
   };

   return (
      <>
         <NavBar />
         <Formik
            initialValues={{
               complainerDetails: "",
               pseudonym: "",
               followUpEmail: "",
               complaintType: "anonima",
               branch: "",
               complainerType: "",
               category: "",
               date: "",
               body: "",
               witnesses: [],
               specificPlace: "",
               directlyAffected: null,
               present: null,
               offenderDetails: [],
               checked: false,
            }}
            onSubmit={handleNewComplaintFormik}
            validationSchema={validationSchema}
         >
            {(formProps: FormikProps<any>) => (
               <Form>
                  <Container maxWidth={"md"} sx={{ backgroundColor: "white", boxShadow: 3, pt: 1 }}>
                     <CssBaseline />
                     <Box display={"flex"} justifyContent={"space-between"} flexDirection={{ xs: "column", md: "row" }}>
                        <NewComplaintBar text={`Información acerca de su ${type}`} />
                        {type === "denuncia" && (
                           <Button
                              variant="text"
                              size="small"
                              endIcon={<HelpIcon sx={{ color: "gray" }} />}
                              onClick={openManual}
                           >
                              ¿Cómo hago mi denuncia?
                           </Button>
                        )}
                     </Box>
                     <Paper elevation={0} sx={{ p: 1, borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
                        {type !== "queja" && (
                           <div>
                              <Box sx={fieldSx}>
                                 <Typography fontWeight={"bold"} variant="body1" id="category-typography">
                                    Categoría
                                 </Typography>
                                 <InputSelectField id="category-select" name="category" variant="standard">
                                    {type === "denuncia"
                                       ? categoryOptions.map((e) => (
                                            <MenuItem key={e} value={e}>
                                               <ListItemText primary={e} />
                                            </MenuItem>
                                         ))
                                       : company.suggestions
                                            .map((suggestion) => (
                                               <MenuItem key={suggestion} value={suggestion}>
                                                  <ListItemText primary={suggestion} />
                                               </MenuItem>
                                            ))
                                            .concat(
                                               <MenuItem key={"Otro"} value={"Otro"}>
                                                  <ListItemText primary={"Otro"} />
                                               </MenuItem>,
                                            )}
                                 </InputSelectField>
                              </Box>
                              <Divider sx={{ my: 3 }} />
                           </div>
                        )}

                        <Box sx={fieldSx}>
                           <Typography fontWeight={"bold"} variant="body1" id="complainerType-typography">
                              {formProps.values.complainerDetails === ""
                                 ? `Hacer mi ${type} no anónima`
                                 : "Volver a denuncia anónima"}
                           </Typography>
                           <Button
                              variant="outlined"
                              data-cy="complainterBtn"
                              onClick={() => {
                                 if (formProps.values.complaintType === "anonima") {
                                    setNonAnonymousFormVisible(true);
                                 } else {
                                    formProps.setValues({
                                       ...formProps.values,
                                       complaintType: "anonima",
                                       complainerDetails: "",
                                       pseudonym: "",
                                       followUpEmail: "",
                                    });
                                 }
                              }}
                           >
                              {formProps.values.complainerDetails === ""
                                 ? "Agregar mi información"
                                 : "Quitar mi información"}
                           </Button>
                        </Box>
                        <Collapse in={formProps.values.complainerDetails !== ""}>
                           <Box sx={{ backgroundColor: "#F1F1F190", p: 1, borderRadius: 2 }}>
                              <Typography px={1}>
                                 Mi información: {formProps.values.complainerDetails.name}&nbsp;
                                 {formProps.values.complainerDetails.lastName}
                              </Typography>
                           </Box>
                        </Collapse>
                        <Box sx={fieldSx}>
                           <Typography fontWeight={"bold"} variant="body1" id="pseudonym-typography">
                              Pseudónimo
                           </Typography>
                           <InputTextField
                              disabled={formProps.values.complaintType === "no anonima"}
                              type="text"
                              id="pseudonym"
                              name="pseudonym"
                              fullWidth={false}
                              variant="standard"
                              value={formProps.values.pseudonym}
                           />
                        </Box>
                        <Box sx={fieldSx}>
                           <Typography fontWeight={"bold"} variant="body1" id="followUpEmail-label">
                              Correo de seguimiento
                           </Typography>
                           <InputTextField
                              disabled={formProps.values.complaintType === "no anonima"}
                              type="text"
                              id="followUpEmail"
                              name="followUpEmail"
                              fullWidth={false}
                              variant="standard"
                              value={formProps.values.followUpEmail}
                              endAdornment={
                                 <Tooltip title="Este correo se utiliza solamente para enviarte notificaciones y darte el correcto seguimiento.">
                                    <InfoIcon color="action" />
                                 </Tooltip>
                              }
                           />
                        </Box>
                        <Divider variant="middle" flexItem sx={{ mt: 2, mb: 1 }} />
                        {type !== "queja" && (
                           <Box sx={fieldSx}>
                              {
                                 <div>
                                    <Typography fontWeight={"bold"} variant="body1" id="complainerType-typography">
                                       Sucursal/establecimiento
                                    </Typography>
                                 </div>
                              }
                              <div>
                                 <InputSelectField
                                    id="branch-select"
                                    name="branch"
                                    clearname="branch"
                                    variant="standard"
                                 >
                                    {company.branches.map((e) => (
                                       <MenuItem key={e.id} value={e.id}>
                                          <ListItemText primary={e.name} />
                                       </MenuItem>
                                    ))}
                                 </InputSelectField>
                              </div>
                           </Box>
                        )}
                        <Box sx={fieldSx}>
                           <div>
                              <Typography fontWeight={"bold"} variant="body1" id="complainerType-typography">
                                 {type === "denuncia"
                                    ? "¿Qué relación tiene con la organización denunciada?"
                                    : "¿Qué relación tiene con la organización?"}
                              </Typography>
                           </div>
                           <div>
                              <InputSelectField id="complainerType-select" name="complainerType" variant="standard">
                                 {complainerTypeOptions.map((e) => (
                                    <MenuItem key={e} value={e}>
                                       <ListItemText primary={e} />
                                    </MenuItem>
                                 ))}
                              </InputSelectField>
                           </div>
                        </Box>
                        {type === "denuncia" && (
                           <Box sx={fieldSx}>
                              <Typography fontWeight={"bold"} variant="body1" id="date-typography">
                                 Fecha del incidente
                              </Typography>
                              <InputTextField
                                 type="date"
                                 id="date"
                                 name="date"
                                 fullWidth={false}
                                 variant="standard"
                                 value={formProps.values.date}
                              />
                           </Box>
                        )}
                        {type !== "queja" && <Divider flexItem sx={{ my: 3 }} />}
                        <Box display="flex" flexDirection="column" m={1}>
                           <Typography fontWeight={"bold"} variant="body1" id="body-typography">
                              {`Describa aquí su ${type}`}
                           </Typography>
                           <InputTextField
                              placeholder={descriptionHolder}
                              multiline
                              type="text"
                              id="body"
                              name="body"
                              fullWidth={false}
                              variant="outlined"
                              value={formProps.values.body}
                              maxLength={2000}
                           />
                        </Box>
                        {type === "denuncia" && (
                           <div>
                              <Box sx={fieldSx}>
                                 <Typography fontWeight={"bold"}>Testigos o personas que conocen el caso</Typography>
                                 <Button
                                    variant="outlined"
                                    data-cy="addWitnessBtn"
                                    onClick={() => {
                                       if (formProps.values.witnesses.length < 2) {
                                          setWitnessFormOpen(true);
                                          return;
                                       }
                                       showSnackBar("Límite de 2 testigos alcanzado.", true);
                                    }}
                                 >
                                    Agregar Testigo
                                 </Button>
                              </Box>
                              <Box sx={fieldSx}>
                                 <ShowAddedInvolved
                                    array={formProps.values.witnesses}
                                    formProps={formProps}
                                    isOffender={false}
                                    onEdit={handleEditWitness}
                                 />
                              </Box>
                              <Box sx={fieldSx}>
                                 <Typography fontWeight={"bold"} variant="body1" id="specificPlace-typography">
                                    Lugar en donde sucedieron los actos
                                 </Typography>
                                 <InputTextField
                                    type="text"
                                    id="specificPlace"
                                    name="specificPlace"
                                    fullWidth={false}
                                    variant="standard"
                                    value={formProps.values.specificPlace}
                                 />
                              </Box>
                           </div>
                        )}
                        {type !== "sugerencia" && (
                           <div>
                              <Divider variant="middle" flexItem sx={{ mt: 2, mb: 1 }} />
                              <Box sx={fieldSx}>
                                 <Typography
                                    sx={{ mr: 2 }}
                                    fontWeight={"bold"}
                                    variant="body1"
                                    id="specificPlace-label"
                                 >
                                    {type === "queja" ? "Adjuntar archivos" : "Evidencias"}
                                 </Typography>
                                 <input
                                    ref={evidenceRef}
                                    type="file"
                                    name="evidence"
                                    accept="image/png, image/jpeg, .pdf ,.mp3, audio/*, video/mp4, video/x-m4v, video/*"
                                    multiple
                                    style={{
                                       borderRadius: 8,
                                       padding: 8,
                                       marginRight: 8,
                                       width: "80%",
                                       backgroundColor: "#F1F1F1",
                                    }}
                                    onChange={onEvidenceUpload}
                                 />
                              </Box>
                              <Collapse in={hasEvidence}>
                                 <Box sx={{ p: 1 }}>
                                    {evidenceBlob.map((file, index) => (
                                       <Card
                                          key={file.name}
                                          sx={{
                                             display: "flex",
                                             bgcolor: "#F9F9F9",
                                             flexDirection: "row",
                                             justifyContent: "space-between",
                                             mt: 1,
                                             boxShadow: 0,
                                          }}
                                       >
                                          <CardContent>
                                             <Typography sx={{ fontSize: 14, flexWrap: "nowrap" }}>
                                                {file.name}
                                             </Typography>
                                          </CardContent>
                                          <CardActions>
                                             <IconButton
                                                onClick={() => {
                                                   setEvidenceBlob((prev) => prev.filter((item, i) => i !== index));
                                                   setEvidences((prev) => prev.filter((item, i) => i !== index));
                                                }}
                                             >
                                                <DeleteIcon sx={{ color: "red" }} />
                                             </IconButton>
                                          </CardActions>
                                       </Card>
                                    ))}
                                 </Box>
                              </Collapse>
                           </div>
                        )}
                        {type === "denuncia" && (
                           <div>
                              <Divider variant="middle" flexItem sx={{ mt: 2, mb: 1 }} />
                              <FormControl
                                 sx={radioFieldSx}
                                 error={
                                    formProps.touched.directlyAffected && Boolean(formProps.errors.directlyAffected)
                                 }
                                 variant="standard"
                              >
                                 <FormLabel required style={formLabelStyle} id="directlyAffected-radios">
                                    ¿Se le afectó directamente?
                                 </FormLabel>
                                 <Box>
                                    <RadioGroup
                                       row
                                       aria-labelledby="directlyAffected-radios"
                                       data-cy="directlyAffectedRadio"
                                       name="directlyAffected"
                                       value={formProps.values.directlyAffected}
                                       onChange={formProps.handleChange}
                                    >
                                       <FormControlLabel value={true} control={<Radio />} label="Si" />
                                       <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                 </Box>
                              </FormControl>
                              <FormControl
                                 sx={radioFieldSx}
                                 error={formProps.touched.present && Boolean(formProps.errors.present)}
                                 variant="standard"
                              >
                                 <FormLabel required style={formLabelStyle} id="present-radios">
                                    ¿Estaba presente mientras ocurrió el incidente?
                                 </FormLabel>
                                 <Box>
                                    <RadioGroup
                                       row
                                       aria-labelledby="present-radios"
                                       data-cy="presentRadio"
                                       name="present"
                                       value={formProps.values.present}
                                       onChange={formProps.handleChange}
                                    >
                                       <FormControlLabel value={true} control={<Radio />} label="Si" />
                                       <FormControlLabel value={false} control={<Radio />} label="No" />
                                    </RadioGroup>
                                 </Box>
                              </FormControl>
                              <Divider variant="middle" flexItem sx={{ mt: 2, mb: 1 }} />
                              <Box sx={fieldSx}>
                                 <Typography fontWeight={"bold"} variant="body1" id="verificationCode-label">
                                    Infractor
                                 </Typography>
                                 <Button
                                    variant="outlined"
                                    data-cy="addOffenderBtn"
                                    onClick={() => {
                                       if (formProps.values.offenderDetails.length < 2) {
                                          setOffenderFormOpen(true);
                                          return;
                                       }
                                       showSnackBar("Límite de 2 infractores alcanzado.", true);
                                    }}
                                 >
                                    Agregar Infractor
                                 </Button>
                              </Box>
                              <Box sx={fieldSx}>
                                 <ShowAddedInvolved
                                    array={formProps.values.offenderDetails}
                                    formProps={formProps}
                                    isOffender={true}
                                    onEdit={handleEditOffender}
                                 />
                              </Box>
                           </div>
                        )}
                     </Paper>
                     <ComplainerDetailsModal
                        open={nonAnonymousFormVisible}
                        setOpen={setNonAnonymousFormVisible}
                        formProps={formProps}
                     />
                     <WitnessInfractorModal
                        isOffender={true}
                        title={editOffenderIndex !== null ? "Editar Infractor" : "Agregar Infractor"}
                        open={offenderFormOpen}
                        initialData={
                           editOffenderIndex !== null ? formProps.values.offenderDetails[editOffenderIndex] : null
                        }
                        setOpen={(val) => {
                           setOffenderFormOpen(val);
                           if (!val) setEditOffenderIndex(null);
                        }}
                        onSave={(values) => {
                           if (editOffenderIndex !== null) {
                              const newOffenders = [...formProps.values.offenderDetails];
                              newOffenders[editOffenderIndex] = values;
                              formProps.setFieldValue("offenderDetails", newOffenders);
                           } else {
                              formProps.setFieldValue("offenderDetails", [...formProps.values.offenderDetails, values]);
                           }
                           setEditOffenderIndex(null);
                        }}
                     />
                     <WitnessInfractorModal
                        isOffender={false}
                        title={editWitnessIndex !== null ? "Editar Testigo" : "Agregar Testigo"}
                        open={witnessFormOpen}
                        initialData={editWitnessIndex !== null ? formProps.values.witnesses[editWitnessIndex] : null}
                        setOpen={(val) => {
                           setWitnessFormOpen(val);
                           if (!val) setEditWitnessIndex(null);
                        }}
                        onSave={(values) => {
                           if (editWitnessIndex !== null) {
                              const newWitnesses = [...formProps.values.witnesses];
                              newWitnesses[editWitnessIndex] = values;
                              formProps.setFieldValue("witnesses", newWitnesses);
                           } else {
                              formProps.setFieldValue("witnesses", [...formProps.values.witnesses, values]);
                           }
                           setEditWitnessIndex(null);
                        }}
                     />
                     <ThankYouModal
                        open={thankyouModalVisible}
                        setOpen={setThankyouModalVisible}
                        company={company}
                        reportNumber={complaintResultInfo.rn}
                        verificationCode={complaintResultInfo.vc}
                        type={type}
                     />
                     <SnackBarComponent />
                  </Container>
                  <Container maxWidth={"md"}>
                     <Stack direction={"row"} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                        <InputCheckBox
                           datacy={"privacyCheck"}
                           id={"checked"}
                           name={"checked"}
                           label={
                              <Typography sx={{ fontSize: { xs: "15px", md: "auto" } }}>
                                 He leído y acepto el&nbsp;{" "}
                                 <a
                                    href={`../Aviso-de-privacidad`}
                                    rel="noreferrer"
                                    target="_blank"
                                    style={{
                                       color: "#162c44",
                                       textDecoration: "none",
                                    }}
                                 >
                                    <strong>Aviso de privacidad</strong>
                                 </a>
                              </Typography>
                           }
                        />
                        <Box my={2}>
                           <Button
                              data-cy="sendBtn"
                              type="submit"
                              variant="outlined"
                              sx={{ bgcolor: company.color, color: "white", fontSize: "16px" }}
                           >
                              {!isSendingComplaint ? "Enviar" : <CircularProgress size={24} color="success" />}
                           </Button>
                        </Box>
                     </Stack>
                  </Container>
               </Form>
            )}
         </Formik>
      </>
   );
};

interface ShowAddedInvolvedProps {
   array: any[];
   formProps: any;
   isOffender: boolean;
   onEdit: (index: number) => void;
}

const ShowAddedInvolved = (props: ShowAddedInvolvedProps) => {
   return (
      <Box sx={{ alignSelf: "flex-start", width: "100%" }}>
         {props.array.map((o, i) => (
            <Box
               display="flex"
               justifyContent="space-between"
               alignItems="center"
               key={`o${i}`}
               sx={{ bgcolor: "#F1F1F1", m: 0.5, p: 1, borderRadius: 1 }}
            >
               <Box sx={{ flex: 1, pr: 1 }}>
                  <Box display="flex">
                     <Box mr={2}>
                        {o.name !== "" ? (
                           <>
                              Nombre: {o.name} {o.lastName}
                           </>
                        ) : props.isOffender ? (
                           <>Infractor {i + 1}</>
                        ) : (
                           <>Testigo {i + 1}</>
                        )}
                     </Box>
                     <Box>Género: {o.gender}</Box>
                  </Box>
                  {o.observations ? <Box>Observaciones: {o.observations}</Box> : <></>}
               </Box>

               <Box display="flex">
                  <IconButton color="primary" sx={{ ml: 1 }} onClick={() => props.onEdit(i)}>
                     <EditIcon />
                  </IconButton>

                  <IconButton
                     color="warning"
                     sx={{ ml: 1 }}
                     onClick={() => {
                        const fieldName = props.isOffender ? "offenderDetails" : "witnesses";
                        const newArray = props.array.filter((_, index) => index !== i);
                        props.formProps.setFieldValue(fieldName, newArray);
                     }}
                  >
                     <ClearIcon />
                  </IconButton>
               </Box>
            </Box>
         ))}
      </Box>
   );
};

const fieldSx = {
   display: "flex",
   alignItems: "center",
   justifyContent: "space-between",
   m: 1,
};

const radioFieldSx = {
   display: "flex",
   flexDirection: "row",
   justifyContent: "space-between",
   alignItems: "center",
   m: 1,
};

const formLabelStyle = {
   flex: 1,
   color: "black",
   fontWeight: 600,
   animation: "none",
};
